import { useMutation, useQuery } from '@tanstack/react-query'
import {
  bulkValidateInventories,
  fetchInventory,
  fetchStoreInventories,
  fetchStoreInventoriesAggregatedStats,
  updateInventoryDescription,
  updateInventoryFinishedStatus,
  updateInventoryStatus,
  updateUnScannableProducts,
} from '../queries/inventories.queries'
import { fetchReapproNeedsByStoreAndDate } from '../queries/stores.queries'

export const useInventory = (id?: number) =>
  useQuery({
    queryKey: ['inventory', id],
    queryFn: () => fetchInventory(id!),
    enabled: !!id,
  })

export const useStoreInventories = (id: number) =>
  useQuery({
    queryKey: ['storeInventories', id],
    queryFn: () => fetchStoreInventories(id),
  })

export const useStoreInventoriesAggregatedStats = (
  storeId: number,
  startDate: string | null,
  endDate: string | null,
) =>
  useQuery({
    queryKey: ['storeInventoriesAggregatedStats', storeId, startDate, endDate],
    queryFn: () => fetchStoreInventoriesAggregatedStats(storeId, startDate!, endDate!),
    enabled: !!storeId && !!startDate && !!endDate,
    // Validated inventories are immutable, so cache aggressively across navigations.
    staleTime: 5 * 60 * 1000,
  })

export const useUpdateUnScannableProducts = () =>
  useMutation({
    mutationFn: updateUnScannableProducts,
  })

export const useUpdateInventoryDescription = () =>
  useMutation({
    mutationFn: updateInventoryDescription,
  })

export const useUpdateInventoryStatus = () =>
  useMutation({
    mutationFn: updateInventoryStatus,
  })

export const useBulkValidateInventories = () =>
  useMutation({
    mutationFn: bulkValidateInventories,
  })

export const useUpdateInventoryFinishedStatus = () =>
  useMutation({
    mutationFn: updateInventoryFinishedStatus,
  })

export const useReapproNeeds = (
  storeReference: number | string | undefined,
  date: string | null,
  enabled = true,
) =>
  useQuery({
    queryKey: ['reapproNeeds', storeReference, date],
    queryFn: () => fetchReapproNeedsByStoreAndDate(String(storeReference!), date!),
    enabled: enabled && !!storeReference && !!date,
    staleTime: 30_000,
  })
